
import React, { useState, useEffect } from 'react';
import { Exercise } from '@/models/LearnTypes';
import { cn } from '@/lib/utils';

interface FlashCardStackProps {
  exercises: Exercise[];
  currentIndex: number;
  onCardClick?: (exercise: Exercise) => void;
  showCardContent?: boolean;
  isAnimating?: boolean;
  
  // Add compatibility with FlashCardDisplay component
  stackedCards?: Exercise[];
  frontContent?: string;
  backContent?: string;
  initialLoad?: boolean;
  deckAnimationActive?: boolean;
  isNextCardOnly?: boolean;
  showAsTopCard?: boolean;
}

const FlashCardStack: React.FC<FlashCardStackProps> = ({
  exercises,
  currentIndex,
  onCardClick,
  showCardContent = true,
  isAnimating = false,
  
  // Add support for the alternate props from FlashCardDisplay
  stackedCards,
  frontContent,
  backContent,
  initialLoad,
  deckAnimationActive,
  isNextCardOnly,
  showAsTopCard
}) => {
  const [stackCards, setStackCards] = useState<Exercise[]>([]);
  
  // Update the stack cards when exercises or currentIndex changes
  useEffect(() => {
    // Use stackedCards prop if provided, otherwise use exercises
    const sourceCards = stackedCards || exercises;
    
    // Only process if we have cards to work with
    if (sourceCards.length === 0) return;
    
    // Get the current card and the next 3 cards (if available)
    let newStackCards = [];
    
    // Add current card
    if (sourceCards[currentIndex]) {
      newStackCards.push(sourceCards[currentIndex]);
    }
    
    // Add next 3 cards for the stack appearance
    for (let i = 1; i <= 3; i++) {
      const nextIndex = currentIndex + i;
      if (nextIndex < sourceCards.length) {
        newStackCards.push(sourceCards[nextIndex]);
      }
    }
    
    // Update the state
    setStackCards(newStackCards);
  }, [exercises, stackedCards, currentIndex]);
  
  // Don't render if no cards
  if (stackCards.length === 0) return null;
  
  return (
    <div className="relative w-full h-48 mt-2 mb-8">
      {stackCards.map((card, idx) => {
        // The currentCard is always at index 0 in the stackCards array
        const isCurrentCard = idx === 0;
        
        // Calculate offset and z-index for stack effect
        const offset = idx * 4;
        const zIndex = 10 - idx;
        
        return (
          <div
            key={`${card.id}-${idx}`}
            className={cn(
              "absolute top-0 left-0 w-full bg-white rounded-lg shadow-md transition-all",
              isAnimating && isCurrentCard && "translate-x-full opacity-0", // Slide out animation for current card
              isCurrentCard ? "cursor-pointer shadow-lg" : "shadow-sm",
            )}
            style={{
              height: `calc(100% - ${offset}px)`,
              transform: isCurrentCard ? 'none' : `translateY(${offset}px) scale(${1 - idx * 0.05})`,
              zIndex,
              opacity: isCurrentCard ? 1 : 1 - idx * 0.2,
            }}
            onClick={() => isCurrentCard && onCardClick && onCardClick(card)}
          >
            {isCurrentCard && showCardContent && (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <div className="font-medium text-xl">{card.word}</div>
                <div className="text-muted-foreground mt-1">
                  {/* Safely access hints property if it exists */}
                  {card.hints ? card.hints.join(', ') : ''}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FlashCardStack;
